import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CITY_TIERS, BUCKET_COLORS, THREE_BUCKETS, FOUR_BUCKETS, INCOME_TYPES, makeIncomeSource } from "@/lib/constants";
import { formatCurrency } from "@/lib/format";
import { motion } from "framer-motion";
import { Wallet, TrendingUp, Layers, AlertCircle, PiggyBank, Plus, Trash2, Coins } from "lucide-react";

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-xs uppercase font-semibold tracking-wider text-[#8A9790]">{label}</Label>
      {hint && <span className="text-xs font-mono text-[#5C6A64]">{hint}</span>}
    </div>
    {children}
  </div>
);

const SliderRow = ({ testid, value, min, max, step, onChange, suffix = "", display }) => (
  <div className="flex items-center gap-4">
    <Slider
      data-testid={testid}
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
      className="flex-1"
    />
    <div className="w-16 text-right font-mono font-semibold text-[#1A231F] text-sm">
      {display !== undefined ? display : value}
      {suffix}
    </div>
  </div>
);

export const InputPanel = ({ inputs, setInputs, result }) => {
  const cur = inputs.currency;
  const upd = (patch) => setInputs((p) => ({ ...p, ...patch }));

  const bucketSum = inputs.buckets.reduce((s, b) => s + b.allocation, 0);
  const updBucket = (i, patch) =>
    setInputs((p) => ({
      ...p,
      buckets: p.buckets.map((b, idx) => (idx === i ? { ...b, ...patch } : b)),
    }));

  const setBucketCount = (n) =>
    setInputs((p) => ({
      ...p,
      numBuckets: n,
      buckets: (n === 4 ? FOUR_BUCKETS : THREE_BUCKETS).map((b) => ({ ...b })),
    }));

  const addIncome = () =>
    setInputs((p) => ({
      ...p,
      incomeSources: [...(p.incomeSources || []), makeIncomeSource("Rental", { startAge: p.retirementAge, monthly: 0 })],
    }));
  const updIncome = (id, patch) =>
    setInputs((p) => ({ ...p, incomeSources: p.incomeSources.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
  const removeIncome = (id) =>
    setInputs((p) => ({ ...p, incomeSources: p.incomeSources.filter((s) => s.id !== id) }));
  const numInput = "w-full h-11 rounded-lg bg-[#FBF9F5] border border-[#E8E3D9] px-3 font-mono text-sm text-[#1A231F] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30";

  return (
    <div className="space-y-6" data-testid="input-panel">
      {/* Ages */}
      <section className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6 space-y-5">
        <h3 className="font-heading text-lg font-semibold text-[#1A231F] flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-[#2D6A4F]" />
          </span>
          Your Timeline
        </h3>

        <Field label="Current Age" hint={`${inputs.currentAge} yrs`}>
          <SliderRow testid="slider-current-age" value={inputs.currentAge} min={18} max={70} step={1}
            onChange={(v) => upd({ currentAge: v, retirementAge: Math.max(v + 1, inputs.retirementAge) })} suffix="" />
        </Field>

        <Field label="Retirement Age" hint={`${inputs.retirementAge} yrs`}>
          <SliderRow testid="slider-retirement-age" value={inputs.retirementAge}
            min={inputs.currentAge + 1} max={80} step={1}
            onChange={(v) => upd({ retirementAge: v, longevityAge: Math.max(v + 1, inputs.longevityAge) })} />
        </Field>

        <Field label="Plan Until Age (Longevity)" hint={`${inputs.longevityAge} yrs`}>
          <SliderRow testid="slider-longevity-age" value={inputs.longevityAge}
            min={inputs.retirementAge + 1} max={105} step={1} onChange={(v) => upd({ longevityAge: v })} />
        </Field>
      </section>

      {/* Money */}
      <section className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6 space-y-5">
        <h3 className="font-heading text-lg font-semibold text-[#1A231F] flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-[#D99B26]/15 flex items-center justify-center">
            <Wallet className="h-4 w-4 text-[#B45309]" />
          </span>
          Money & Expenses
        </h3>

        <Field label="Current Retirement Corpus">
          <Input data-testid="input-corpus" type="number" value={inputs.currentCorpus}
            onChange={(e) => upd({ currentCorpus: Number(e.target.value) })}
            className="font-mono h-11 bg-[#FBF9F5] border-[#E8E3D9]" />
          <p className="text-xs text-[#8A9790]">{formatCurrency(inputs.currentCorpus, cur)}</p>
        </Field>

        <Field label="Current Monthly Expenses">
          <Input data-testid="input-monthly-expense" type="number" value={inputs.monthlyExpenses}
            onChange={(e) => upd({ monthlyExpenses: Number(e.target.value) })}
            className="font-mono h-11 bg-[#FBF9F5] border-[#E8E3D9]" />
          <p className="text-xs text-[#8A9790]">{formatCurrency(inputs.monthlyExpenses, cur)} / month today</p>
        </Field>

        <Field label="Extra Annual Spend (travel / events)">
          <Input data-testid="input-annual-extra" type="number" value={inputs.annualExtra}
            onChange={(e) => upd({ annualExtra: Number(e.target.value) })}
            className="font-mono h-11 bg-[#FBF9F5] border-[#E8E3D9]" />
          <p className="text-xs text-[#8A9790]">{formatCurrency(inputs.annualExtra, cur)} / year today</p>
        </Field>

        <div className="border-t border-dashed border-[#E8E3D9] pt-4 space-y-4">
          <div className="flex items-center gap-2 text-[#2D6A4F]">
            <PiggyBank className="h-4 w-4" />
            <span className="text-xs uppercase font-semibold tracking-wider">Savings Runway (before retirement)</span>
          </div>
          <Field label="Monthly Contribution">
            <Input data-testid="input-monthly-contribution" type="number" value={inputs.monthlyContribution}
              onChange={(e) => upd({ monthlyContribution: Number(e.target.value) })}
              className="font-mono h-11 bg-[#FBF9F5] border-[#E8E3D9]" />
            <p className="text-xs text-[#8A9790]">{formatCurrency(inputs.monthlyContribution, cur)} / month invested until age {inputs.retirementAge}</p>
          </Field>
          <Field label="Annual Step-up in Contribution" hint={`${inputs.contributionStepUp}%`}>
            <SliderRow testid="slider-contribution-stepup" value={inputs.contributionStepUp} min={0} max={15} step={1}
              onChange={(v) => upd({ contributionStepUp: v })} suffix="%" />
          </Field>
        </div>

        <Field label="Inflation Assumed" hint={`${inputs.inflation}%`}>
          <SliderRow testid="slider-inflation" value={inputs.inflation} min={0} max={12} step={0.5}
            onChange={(v) => upd({ inflation: v })} suffix="%" />
        </Field>

        <Field label="Inflation Commences From" hint={`Year ${inputs.inflationStartYear ?? 1}`}>
          <SliderRow testid="slider-inflation-start" value={inputs.inflationStartYear ?? 1} min={1} max={5} step={1}
            onChange={(v) => upd({ inflationStartYear: v })} display={`Yr ${inputs.inflationStartYear ?? 1}`} />
          <p className="text-xs text-[#8A9790]">Expenses stay flat until this retirement year, then start rising with inflation.</p>
        </Field>
      </section>

      {/* City tier */}
      <section className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6 space-y-4">
        <h3 className="font-heading text-lg font-semibold text-[#1A231F]">Retirement City</h3>
        <div className="grid grid-cols-3 gap-3" data-testid="city-tier-selector">
          {CITY_TIERS.map((t) => {
            const active = inputs.cityTier === t.id;
            return (
              <button key={t.id} data-testid={`tier-${t.id}`} onClick={() => upd({ cityTier: t.id })}
                className={`rounded-xl border p-3 text-left transition-all duration-300 ${
                  active
                    ? "border-[#D96B43] bg-[#D96B43]/8 shadow-sm ring-1 ring-[#D96B43]/30"
                    : "border-[#E8E3D9] bg-[#FBF9F5] hover:border-[#C8BFB0]"
                }`}>
                <div className="font-heading font-bold text-[#1A231F] text-sm">{t.label}</div>
                <div className="text-[11px] text-[#8A9790] leading-tight mt-0.5">{t.sub}</div>
                <div className={`text-[11px] font-semibold mt-1 ${active ? "text-[#D96B43]" : "text-[#5C6A64]"}`}>{t.note}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Strategy */}
      <section className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6 space-y-5">
        <h3 className="font-heading text-lg font-semibold text-[#1A231F] flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-[#D96B43]/12 flex items-center justify-center">
            <Layers className="h-4 w-4 text-[#D96B43]" />
          </span>
          Withdrawal Strategy
        </h3>

        <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F5F2EB] p-1" data-testid="strategy-toggle">
          {[
            { id: "swp", label: "Simple SWP" },
            { id: "bucket", label: "Bucket Strategy" },
          ].map((s) => {
            const active = inputs.strategy === s.id;
            return (
              <button key={s.id} data-testid={`strategy-${s.id}`} onClick={() => upd({ strategy: s.id })}
                className={`relative rounded-lg py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  active ? "text-white" : "text-[#5C6A64]"
                }`}>
                {active && (
                  <motion.span layoutId="strategy-pill" className="absolute inset-0 rounded-lg bg-[#1E2B25]"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                )}
                <span className="relative">{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="pt-1">
          <Label className="text-xs uppercase font-semibold tracking-wider text-[#8A9790]">Corpus withdrawal mode</Label>
          <div className="mt-2 grid grid-cols-2 gap-2 rounded-xl bg-[#F5F2EB] p-1" data-testid="withdrawal-mode-toggle">
            {[
              { id: "gap", label: "Fund the gap" },
              { id: "fixed", label: "Fixed % SWP" },
            ].map((m) => {
              const active = (inputs.withdrawalMode || "gap") === m.id;
              return (
                <button key={m.id} data-testid={`wmode-${m.id}`} onClick={() => upd({ withdrawalMode: m.id })}
                  className={`relative rounded-lg py-2 text-xs sm:text-sm font-semibold transition-colors duration-300 ${active ? "text-white" : "text-[#5C6A64]"}`}>
                  {active && <motion.span layoutId="wmode-pill" className="absolute inset-0 rounded-lg bg-[#2D6A4F]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
                  <span className="relative">{m.label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-[#8A9790] mt-2">
            {(inputs.withdrawalMode || "gap") === "gap"
              ? "Corpus is drawn only to cover what your other income doesn't — it lasts far longer."
              : "Withdraw a set % of the corpus each year as income, on top of your other sources."}
          </p>
          {(inputs.withdrawalMode || "gap") === "fixed" && (
            <div className="mt-3">
              <Field label="Annual SWP rate" hint={`${inputs.swpRate}%`}>
                <SliderRow testid="slider-swp-rate" value={inputs.swpRate} min={1} max={12} step={0.5}
                  onChange={(v) => upd({ swpRate: v })} suffix="%" />
              </Field>
            </div>
          )}
        </div>

        {inputs.strategy === "swp" ? (
          <Field label="Portfolio Expected Return" hint={`${inputs.swpReturn}%`}>
            <SliderRow testid="slider-swp-return" value={inputs.swpReturn} min={2} max={16} step={0.5}
              onChange={(v) => upd({ swpReturn: v })} suffix="%" />
          </Field>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F5F2EB] p-1" data-testid="bucket-count-toggle">
              {[3, 4].map((n) => {
                const active = inputs.buckets.length === n;
                return (
                  <button key={n} data-testid={`bucket-count-${n}`} onClick={() => setBucketCount(n)}
                    className={`relative rounded-lg py-2 text-sm font-semibold transition-colors duration-300 ${
                      active ? "text-white" : "text-[#5C6A64]"
                    }`}>
                    {active && (
                      <motion.span layoutId="bucket-count-pill" className="absolute inset-0 rounded-lg bg-[#2D6A4F]"
                        transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                    )}
                    <span className="relative">{n}-Bucket{n === 4 ? " + Legacy" : ""}</span>
                  </button>
                );
              })}
            </div>
            {inputs.buckets.map((b, i) => (
              <div key={i} className="rounded-xl border border-[#E8E3D9] p-4 bg-[#FBF9F5] space-y-3"
                data-testid={`bucket-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: BUCKET_COLORS[i] }} />
                  <span className="font-heading font-semibold text-sm text-[#1A231F]">
                    Bucket {i + 1} · {b.name}
                  </span>
                </div>
                <Field label="Allocation" hint={`${b.allocation}%`}>
                  <SliderRow testid={`slider-bucket-${i}-alloc`} value={b.allocation} min={0} max={100} step={1}
                    onChange={(v) => updBucket(i, { allocation: v })} suffix="%" />
                </Field>
                <Field label="Expected Return" hint={`${b.return}%`}>
                  <SliderRow testid={`slider-bucket-${i}-return`} value={b.return} min={0} max={18} step={0.5}
                    onChange={(v) => updBucket(i, { return: v })} suffix="%" />
                </Field>
              </div>
            ))}
            <div className={`flex items-center gap-2 text-sm font-semibold rounded-lg px-3 py-2 ${
              bucketSum === 100 ? "text-[#1B4332] bg-[#2D6A4F]/10" : "text-[#991B1B] bg-[#991B1B]/8"
            }`} data-testid="bucket-sum-indicator">
              {bucketSum !== 100 && <AlertCircle className="h-4 w-4" />}
              Total allocation: {bucketSum}% {bucketSum === 100 ? "✓" : "(must equal 100%)"}
            </div>
          </div>
        )}
      </section>

      {/* Other income sources */}
      <section className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6 space-y-4" data-testid="income-section">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-[#1A231F] flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-[#2A6F7F]/10 flex items-center justify-center">
              <Coins className="h-4 w-4 text-[#2A6F7F]" />
            </span>
            Other Income Sources
          </h3>
          <button data-testid="add-income-btn" onClick={addIncome}
            className="flex items-center gap-1 text-sm font-semibold text-[#2D6A4F] hover:text-[#1B4332] rounded-lg px-2.5 py-1.5 hover:bg-[#2D6A4F]/8 transition-colors">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
        <p className="text-xs text-[#8A9790]">Rent, FD interest, PPF, pension & more — blended with your SWP into one monthly paycheck.</p>

        {(inputs.incomeSources || []).length === 0 && (
          <div className="text-sm text-[#8A9790] rounded-lg bg-[#FBF9F5] border border-dashed border-[#E8E3D9] px-4 py-3">
            No extra income yet — your corpus funds all expenses.
          </div>
        )}

        {(inputs.incomeSources || []).map((s, i) => (
          <div key={s.id} data-testid={`income-${i}`} className="rounded-xl border border-[#E8E3D9] p-4 bg-[#FBF9F5] space-y-3">
            <div className="flex items-center gap-2">
              <select data-testid={`income-${i}-type`} value={s.type}
                onChange={(e) => {
                  const meta = INCOME_TYPES.find((t) => t.type === e.target.value);
                  updIncome(s.id, { type: e.target.value, label: meta ? meta.label : e.target.value, growth: meta ? meta.growth : s.growth });
                }}
                className="flex-1 h-10 rounded-lg bg-white border border-[#E8E3D9] px-3 text-sm font-semibold text-[#1A231F] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30">
                {INCOME_TYPES.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
              </select>
              <button data-testid={`income-${i}-remove`} onClick={() => removeIncome(s.id)}
                className="h-10 w-10 flex items-center justify-center rounded-lg text-[#8A9790] hover:text-[#991B1B] hover:bg-[#991B1B]/8 transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <Field label="Monthly amount">
              <input type="number" min={0} data-testid={`income-${i}-monthly`} value={s.monthly}
                onChange={(e) => updIncome(s.id, { monthly: Number(e.target.value) })} className={numInput} />
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Start age">
                <input type="number" min={0} data-testid={`income-${i}-start`} value={s.startAge}
                  onChange={(e) => updIncome(s.id, { startAge: Number(e.target.value) })} className={numInput} />
              </Field>
              <Field label="End age">
                <input type="number" min={0} data-testid={`income-${i}-end`} value={s.endAge ?? ""} placeholder="Life"
                  onChange={(e) => updIncome(s.id, { endAge: e.target.value === "" ? null : Number(e.target.value) })} className={numInput} />
              </Field>
              <Field label="Growth %/yr">
                <input type="number" min={0} data-testid={`income-${i}-growth`} value={s.growth}
                  onChange={(e) => updIncome(s.id, { growth: Number(e.target.value) })} className={numInput} />
              </Field>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};
